import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageSquareText as CommentsIcon } from "lucide-react";
import { LoadingState } from "../../../components/common/LoadingState/index.js";
import { useElementWidthBucket } from "../../../components/layout/use-element-width-bucket.js";
import { useMinWidth } from "../../../components/layout/use-min-width.js";
import { motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { useOrganizations } from "../../organizations/organization-context.js";
import {
  INBOX_PANE_GAP_PX,
  inboxContainerSize,
  inboxLayoutMode,
  showThreadDetailsOpener,
  type InboxLayoutMode,
} from "../inbox-layout.js";
import {
  initialMobileInboxPane,
  MOBILE_INBOX_BREAKPOINT_PX,
  reduceMobileInboxPane,
  type MobileInboxPane,
} from "../mobile-inbox-nav.js";
import { CommentDetailsPane } from "./CommentDetailsPane.js";
import { CommentsListPane } from "./CommentsListPane.js";
import type { CommentSendPolicy } from "./CommentReplyComposer.js";
import { CommentThreadPane } from "./CommentThreadPane.js";
import {
  buildInboxConversationPath,
  parseCommentsViewMode,
  withCommentsViewMode,
  withSelectedCommentId,
  withSelectedPostId,
} from "./comments-url-state.js";
import {
  POST_COMMENTS_DETAILS_DOCKED_WIDTH_PX,
  POST_COMMENTS_DETAILS_DRAWER_WIDTH_PX,
  POST_COMMENTS_HEADER_SHELL,
  POST_COMMENTS_LIST_WIDTH_PX,
  POST_COMMENTS_PAGE_CARD,
} from "./post-comments-layout.js";
import { useCommentsWorkspace } from "./use-comments-workspace.js";
import { useSocialPost } from "./use-social-post.js";

const MOBILE_PANE = "absolute inset-0 w-auto rounded-none border-0 shadow-none";
const MOBILE_PANE_OFFSET_PX = 28;

const paneTransition = { duration: 0.3, ease: [0.2, 0.8, 0.2, 1] as const };
const mobilePaneTransition = { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] as const };

/**
 * Standalone Post Comments workspace — FeedBot-native layout (not an Inbox clone).
 */
export function PostCommentsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { selected } = useOrganizations();

  if (!selected) {
    return <LoadingState message={t("app.loading")} />;
  }

  return <PostCommentsPageContent organizationId={selected.id} />;
}

function PostCommentsPageContent({
  organizationId,
}: {
  organizationId: number;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const isRtl = i18n.dir() === "rtl";
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = parseCommentsViewMode(searchParams.get("viewMode"));
  const selectedCommentId = searchParams.get("commentId");
  const selectedPostId = searchParams.get("postId");

  const [sendPolicy, setSendPolicy] = useState<CommentSendPolicy>("send_and_handle");
  const comments = useCommentsWorkspace(organizationId, {
    active: true,
    viewMode,
    commentId: selectedCommentId,
    postId: selectedPostId,
  });

  const postOnly = selectedPostId != null && selectedCommentId == null;
  const socialPost = useSocialPost(
    organizationId,
    postOnly ? selectedPostId : null,
    t("inbox.unreachable"),
    postOnly,
  );

  /** Center-first: details collapsed by default on desktop. */
  const [dockedDetailsOpen, setDockedDetailsOpen] = useState(false);
  const [drawerDetailsOpen, setDrawerDetailsOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState<MobileInboxPane>(initialMobileInboxPane);
  const detailsCloseRef = useRef<HTMLButtonElement | null>(null);
  const [containerRef, containerSize] = useElementWidthBucket<
    HTMLDivElement,
    ReturnType<typeof inboxContainerSize>
  >(inboxContainerSize);

  const isTabletOrLarger = useMinWidth(MOBILE_INBOX_BREAKPOINT_PX);
  const isMobile = !isTabletOrLarger;
  const layoutMode = inboxLayoutMode({ containerSize, mobileViewport: isMobile });
  const [previousLayoutMode, setPreviousLayoutMode] = useState<InboxLayoutMode>(layoutMode);
  if (previousLayoutMode !== layoutMode) {
    setPreviousLayoutMode(layoutMode);
    if (layoutMode !== "drawer") setDrawerDetailsOpen(false);
    if (layoutMode !== "mobile" && mobilePane === "details") setMobilePane("conversation");
  }

  const detailsOpen = layoutMode === "docked" ? dockedDetailsOpen : drawerDetailsOpen;
  const setDetailsOpen = layoutMode === "docked" ? setDockedDetailsOpen : setDrawerDetailsOpen;
  const hasSelection = comments.thread.threadView.kind === "ready";
  const detailsVisible =
    hasSelection && (layoutMode === "mobile" ? mobilePane === "details" : detailsOpen);
  const drawerOpen = layoutMode === "drawer" && detailsVisible;

  function handleViewModeChange(next: "comments" | "posts"): void {
    setSearchParams((current) => withCommentsViewMode(current, next));
  }

  function handleSelectComment(commentId: string): void {
    setSearchParams((current) => withSelectedCommentId(current, commentId));
    if (isMobile) {
      setMobilePane(reduceMobileInboxPane(mobilePane, { type: "select_conversation" }));
    }
  }

  function handleSelectPost(postId: string): void {
    setSearchParams((current) => withSelectedPostId(current, postId));
    if (isMobile) {
      setMobilePane(reduceMobileInboxPane(mobilePane, { type: "select_conversation" }));
    }
  }

  function handleClearPostFilter(): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("postId");
      return next;
    });
  }

  function handleOpenConversation(conversationId: number): void {
    navigate(buildInboxConversationPath(conversationId));
  }

  function handleMobileBack(): void {
    setMobilePane(reduceMobileInboxPane(mobilePane, { type: "back_to_list" }));
  }

  function handleOpenDetails(): void {
    if (layoutMode === "mobile") {
      setMobilePane((pane) => reduceMobileInboxPane(pane, { type: "open_details" }));
    } else {
      setDetailsOpen(true);
    }
    requestAnimationFrame(() => detailsCloseRef.current?.focus({ preventScroll: true }));
  }

  function handleCloseDetails(): void {
    if (layoutMode === "mobile") {
      setMobilePane((pane) => reduceMobileInboxPane(pane, { type: "back_to_conversation" }));
    } else {
      setDetailsOpen(false);
    }
  }

  function handleDetailsKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (!drawerOpen || event.key !== "Escape" || event.defaultPrevented) return;
    event.preventDefault();
    handleCloseDetails();
  }

  const paneForward = isRtl ? -MOBILE_PANE_OFFSET_PX : MOBILE_PANE_OFFSET_PX;
  const paneBackward = -paneForward;
  const showMobileList = mobilePane === "list";
  const showMobileConversation = mobilePane === "conversation";
  const listHidden = isMobile && !showMobileList;
  const threadHidden = isMobile && !showMobileConversation;
  const threadTransition = reduceMotion ? { duration: 0 } : mobilePaneTransition;

  const listAnimate = isMobile
    ? {
        width: "auto",
        opacity: showMobileList ? 1 : 0,
        x: showMobileList || reduceMotion ? 0 : paneBackward,
      }
    : { width: POST_COMMENTS_LIST_WIDTH_PX, opacity: 1, x: 0 };

  const threadAnimate = isMobile
    ? {
        opacity: showMobileConversation ? 1 : 0,
        x:
          showMobileConversation || reduceMotion
            ? 0
            : mobilePane === "details"
              ? paneBackward
              : paneForward,
      }
    : { opacity: 1, x: 0 };

  function detailsAnimate(): Record<string, number | string> {
    switch (layoutMode) {
      case "docked":
        return {
          width: detailsVisible ? POST_COMMENTS_DETAILS_DOCKED_WIDTH_PX : 0,
          marginInlineStart: detailsVisible ? 0 : -INBOX_PANE_GAP_PX,
          opacity: detailsVisible ? 1 : 0,
          x: 0,
        };
      case "drawer":
        return {
          width: POST_COMMENTS_DETAILS_DRAWER_WIDTH_PX,
          marginInlineStart: 0,
          opacity: 1,
          x: detailsVisible || reduceMotion ? 0 : isRtl ? "-110%" : "110%",
        };
      case "mobile":
        return {
          width: "auto",
          marginInlineStart: 0,
          opacity: detailsVisible ? 1 : 0,
          x: detailsVisible || reduceMotion ? 0 : paneForward,
        };
      default: {
        const exhaustive: never = layoutMode;
        return exhaustive;
      }
    }
  }

  function handleCenterRetry(): void {
    if (postOnly) {
      socialPost.refresh();
      return;
    }
    comments.thread.refresh();
  }

  return (
    <div className="font-feedbot flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden bg-canvas text-ink">
      <header className={POST_COMMENTS_HEADER_SHELL} data-testid="post-comments-page-header">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-grid size-9 shrink-0 place-items-center rounded-xl border border-line/70 bg-surface text-accent shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
            <CommentsIcon size={18} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="m-0 truncate text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground rtl:tracking-normal">
              {t("app.nav.postComments")}
            </h1>
            <p className="m-0 truncate text-[0.75rem] text-muted">
              {t("inbox.comments.page.subtitle")}
            </p>
          </div>
        </div>
      </header>

      <div
        ref={containerRef}
        className={cn(
          "relative flex min-h-0 min-w-0 w-full flex-1 overflow-clip",
          isMobile ? "pb-[env(safe-area-inset-bottom,0px)]" : "gap-2 p-2",
        )}
        data-post-comments-layout={layoutMode}
      >
        <motion.aside
          className={cn(
            POST_COMMENTS_PAGE_CARD,
            "relative flex shrink-0 flex-col",
            isMobile && cn(MOBILE_PANE, "z-[1]"),
          )}
          aria-label={t("inbox.comments.listLabel")}
          aria-hidden={listHidden || undefined}
          inert={listHidden || undefined}
          initial={false}
          animate={listAnimate}
          transition={
            reduceMotion ? { duration: 0 } : isMobile ? mobilePaneTransition : paneTransition
          }
          style={{
            pointerEvents: listHidden ? "none" : "auto",
            visibility: listHidden ? "hidden" : "visible",
          }}
        >
          <div
            className={cn(
              "flex h-full min-h-0 flex-col",
              isMobile ? "w-full" : "w-[238px]",
            )}
          >
            <CommentsListPane
              summary={comments.summary}
              filters={{
                viewMode,
                platform: comments.platform,
                handlingStatus: comments.handlingStatus,
                sort: comments.sort,
                search: comments.search,
                postId: selectedPostId ?? undefined,
              }}
              commentsView={comments.commentsList.listView}
              postsView={comments.postsList.listView}
              hasMore={
                viewMode === "posts" ? comments.postsList.hasMore : comments.commentsList.hasMore
              }
              loadingMore={
                viewMode === "posts"
                  ? comments.postsList.loadingMore
                  : comments.commentsList.loadingMore
              }
              refreshing={
                viewMode === "posts"
                  ? comments.postsList.refreshing
                  : comments.commentsList.refreshing
              }
              syncing={comments.syncing}
              selectedCommentId={selectedCommentId}
              selectedPostId={selectedPostId}
              onViewModeChange={handleViewModeChange}
              onPlatformChange={comments.setPlatform}
              onHandlingStatusChange={comments.setHandlingStatus}
              onSortChange={comments.setSort}
              onSearchChange={comments.setSearch}
              onClearPostFilter={handleClearPostFilter}
              onSync={() => {
                void comments.sync();
              }}
              onSelectComment={handleSelectComment}
              onSelectPost={handleSelectPost}
              onLoadMore={
                viewMode === "posts" ? comments.postsList.loadMore : comments.commentsList.loadMore
              }
              onRetry={
                viewMode === "posts" ? comments.postsList.refresh : comments.commentsList.refresh
              }
            />
          </div>
        </motion.aside>

        <motion.div
          className={cn(
            POST_COMMENTS_PAGE_CARD,
            "relative flex min-w-0 flex-col bg-canvas",
            isMobile ? cn(MOBILE_PANE, "z-[2]") : "flex-1",
          )}
          aria-hidden={threadHidden || undefined}
          inert={threadHidden || undefined}
          initial={false}
          animate={threadAnimate}
          transition={threadTransition}
          style={{
            pointerEvents: threadHidden ? "none" : "auto",
            visibility: threadHidden ? "hidden" : "visible",
          }}
        >
          <CommentThreadPane
            threadView={comments.thread.threadView}
            postView={postOnly ? socialPost.postView : undefined}
            scope={comments.thread.scope}
            onRetry={handleCenterRetry}
            onToggleScope={() =>
              comments.thread.setScope(
                comments.thread.scope === "thread" ? "all_post_comments" : "thread",
              )
            }
            onSelectComment={handleSelectComment}
            onSendPublic={(content, options) =>
              comments.thread.sendPublicReply(content, options)
            }
            onSendPrivate={(content, options) =>
              comments.thread.sendPrivateReply(content, options)
            }
            replyDisabled={comments.thread.workflowPending}
            sendPolicy={sendPolicy}
            onSendPolicyChange={setSendPolicy}
            showDetailsTrigger={showThreadDetailsOpener(layoutMode, detailsVisible)}
            onOpenDetails={handleOpenDetails}
            showMobileBack={isMobile && mobilePane !== "list"}
            onMobileBack={handleMobileBack}
          />
        </motion.div>

        {hasSelection ? (
          <motion.div
            id="post-comments-details"
            className={cn(
              "flex min-h-0 shrink-0 flex-col",
              layoutMode === "drawer" &&
                "absolute inset-y-2 end-2 z-30 max-w-[92%] overflow-hidden rounded-xl shadow-[0_8px_30px_rgba(15,23,42,0.12)]",
              layoutMode === "docked" && POST_COMMENTS_PAGE_CARD,
              layoutMode === "mobile" && "absolute inset-0 z-[3]",
            )}
            role={drawerOpen ? "dialog" : undefined}
            aria-label={
              drawerOpen ? t("inbox.comments.details.panelLabel") : undefined
            }
            aria-hidden={!detailsVisible || undefined}
            inert={!detailsVisible || undefined}
            initial={false}
            animate={detailsAnimate()}
            transition={
              reduceMotion
                ? { duration: 0 }
                : layoutMode === "mobile"
                  ? mobilePaneTransition
                  : paneTransition
            }
            style={{ pointerEvents: detailsVisible ? "auto" : "none" }}
            onKeyDown={handleDetailsKeyDown}
          >
            {comments.thread.threadView.kind === "ready" ? (
              <CommentDetailsPane
                data={comments.thread.threadView.data}
                workflowPending={comments.thread.workflowPending}
                onMarkHandled={comments.thread.markHandled}
                onReopen={comments.thread.reopen}
                onOpenConversation={handleOpenConversation}
                closeControl={layoutMode === "mobile" ? "back" : "collapse"}
                onClose={handleCloseDetails}
                closeButtonRef={detailsCloseRef}
                className={cn(
                  layoutMode === "docked" && "w-[278px]",
                  layoutMode === "drawer" && "rounded-xl border border-line/70",
                  layoutMode === "mobile" && "rounded-none",
                )}
              />
            ) : null}
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}
